import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { PaymentSchedulesDataProvider } from '@services/payments/payments-schedules-data-service/payments-schedules.data.service';
import {
	Option,
	PaymentSchedule,
	PaymentScheduleRequest,
	PaymentScheduleSettings,
	SavePaymentScheduleResponse
} from '@models/invoice.model';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from '@services/helpers/message-service/message.service';

@Component({
	selector: 'payment-schedules-modal',
	template: require('./payment-schedules-modal.html'),
	styles: [require('./payment-schedules-modal.scss')]
})
export class PaymentSchedulesModalComponent implements OnInit, OnDestroy {
	@Input() title: 'New' | 'Edit';
	@Input()
	set paymentSchedule(paymentSchedule: PaymentSchedule | null) {
		if (paymentSchedule) {
			this.newForm();
			this.setInitialValues(paymentSchedule);
			this.editedPaymentSchedule = paymentSchedule;
		}
	}
	@Output() saved = new EventEmitter<SavePaymentScheduleResponse>();
	@Output() error = new EventEmitter<void>();
	paymentScheduleForm: FormGroup;
	buttonDisable = false;
	paymentScheduleSettings: PaymentScheduleSettings;
	editedPaymentSchedule: PaymentSchedule | null = null;
	private unsubscribe$ = new Subject<void>();
	constructor(
		private paymentSchedulesDataService: PaymentSchedulesDataProvider,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		this.newForm();
		this.getSettings();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	get payments(): FormArray | null {
		if (this.paymentScheduleForm) {
			return this.paymentScheduleForm.get('payments') as FormArray;
		}
		return null;
	}

	newForm(): void {
		this.editedPaymentSchedule = null;
		this.paymentScheduleForm = new FormGroup({
			id: new FormControl(null),
			name: new FormControl('', [
				Validators.required,
				Validators.maxLength(255)
			]),
			payments: new FormArray([])
		});
	}

	save(): void {
		if (this.paymentScheduleForm.valid) {
			const formData = this.paymentScheduleForm.getRawValue();
			formData.payments.forEach(payment => {
				payment.amount = payment.amount.value;
				payment.dateItemsAmount = payment.dateItemsAmount.value;
			});
			const requestData: PaymentScheduleRequest = {
				name: formData.name,
				rules: formData.payments
			};
			this.buttonDisable = true;
			if (this.title === 'Edit') {
				requestData.id = formData.id;
				this.paymentSchedulesDataService
					.updatePaymentSchedule(requestData)
					.pipe(finalize(() => (this.buttonDisable = false)))
					.subscribe(
						response => this.onSuccess(response, 's_3'),
						error => this.onError(error)
					);
			} else {
				this.paymentSchedulesDataService
					.createPaymentSchedule(requestData)
					.pipe(finalize(() => (this.buttonDisable = false)))
					.subscribe(
						response => this.onSuccess(response, 's_0'),
						error => this.onError(error)
					);
			}
		}
	}

	addPayment(): void {
		const newPayment = this.newPaymentFormGroup();
		this.setDefaultValues(newPayment);
		this.payments.push(newPayment);
	}

	deletePayment(index: number): void {
		this.payments.removeAt(index);
	}

	getValidatorValue(
		index: number,
		formGroupName: 'amount' | 'dateItemsAmount',
		validatorName: 'maxValue' | 'minValue'
	): number {
		const formGroup = this.payments.at(index).get(formGroupName) as FormGroup;
		return formGroup.get(validatorName).value;
	}

	private onSuccess(
		response: SavePaymentScheduleResponse,
		messageCode: string
	): void {
		if (response) {
			if (response.data) {
				this.messageService.showToaster(
					messageCode,
					'paymentSchedules',
					this.paymentScheduleForm.get('name').value
				);
				this.newForm();
				this.addPayment();
			} else {
				toastr.error(response.result, 'Error');
			}
			this.saved.emit(response);
		}
	}

	private onError(response): void {
		if (response && response.error) {
			const { errorTitle, errorMessage } = response.error;
			this.messageService.showErrorFromBackend(errorTitle, errorMessage);
		} else {
			this.messageService.showToaster('e_0', 'general');
		}
		this.error.emit();
	}

	private getSettings(): void {
		this.paymentSchedulesDataService.getPaymentScheduleSettings().subscribe(
			settings => {
				this.paymentScheduleSettings = settings;
				this.addPayment();
			},
			err => console.log(err)
		);
	}

	private setInitialValues(paymentSchedule: PaymentSchedule): void {
		this.paymentScheduleForm.patchValue(paymentSchedule);
		paymentSchedule.rules.forEach(rule => {
			const payment = this.newPaymentFormGroup();
			payment.patchValue(rule);
			payment
				.get('amount')
				.get('value')
				.setValue(rule.amount);
			payment
				.get('dateItemsAmount')
				.get('value')
				.setValue(rule.dateItemsAmount);
			this.payments.push(payment);
		});
	}

	private setDefaultValues(
		formGroup: FormGroup,
		settingsData = this.paymentScheduleSettings.data
	): void {
		const defaultValues = {
			type: settingsData.scheduleTypes[0].key,
			dateItemsType: settingsData.timeUnits[0].key,
			jobDateDepending: settingsData.dependsTypes[0].key
		};
		formGroup.patchValue(defaultValues);
	}

	private newPaymentFormGroup(): FormGroup {
		const dateItemsAmount = new FormGroup({
			value: new FormControl(null),
			minValue: new FormControl({ value: null, disabled: true })
		});
		const dateItemsType = new FormControl('', [Validators.required]);
		const jobDateDepending = new FormControl('', [Validators.required]);
		const type = new FormControl('', [Validators.required]);
		const amount = new FormGroup({
			value: new FormControl(null),
			maxValue: new FormControl({ value: null, disabled: true })
		});
		const formGroup = new FormGroup({
			id: new FormControl(null),
			amount,
			type,
			dateItemsAmount,
			dateItemsType,
			jobDateDepending
		});
		const { dependsTypes } = this.paymentScheduleSettings.data;
		this.updateValidators(
			dependsTypes,
			jobDateDepending,
			dateItemsAmount,
			dateItemsType
		);
		const { scheduleTypes } = this.paymentScheduleSettings.data;
		this.updateValidators(scheduleTypes, type, amount);
		return formGroup;
	}

	private updateValidators(
		settings: Option[],
		typeControl: FormControl,
		subGroup: FormGroup,
		subTypeControl?: FormControl
	): void {
		typeControl.valueChanges
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((value: string) => {
				const properties = settings.find(option => option.key === value)
					.dueFieldProperties;
				const options = { onlySelf: true, emitEvent: false };
				const valueCtrl = subGroup.get('value') as FormControl;
				if ('minValue' in properties) {
					valueCtrl.setValidators([
						Validators.required,
						Validators.min(properties.minValue)
					]);
					subGroup.get('minValue').setValue(properties.minValue);
					if (properties.enabled) {
						valueCtrl.enable(options);
						subTypeControl.enable(options);
					} else {
						valueCtrl.disable(options);
						valueCtrl.setValue(0);
						subTypeControl.disable(options);
					}
				} else {
					valueCtrl.setValidators([
						Validators.required,
						Validators.min(1),
						Validators.max(properties.maxValue)
					]);
					subGroup.get('maxValue').setValue(properties.maxValue);
				}
				subGroup.updateValueAndValidity({ onlySelf: false, emitEvent: false });
			});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/payment-schedules/modals/payment-schedules-modal/payment-schedules-modal.ts